import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AppDispatch } from "index";
import { toast } from "react-toastify";
import { createSelector } from "reselect";

// 1. Master Form Import (Assuming it's now a generic Sales Order master form)

// 2. Data Types (Assuming these are defined for Sales Order)
// You may need to create or adjust this type structure based on your actual Sales Order payload

// 3. Sales Order Thunks (Need specific thunks for Service Sales Order)

import {
  fetchProductSalesOrderById,
  createProductSalesOrder,
  updateProductSalesOrder,
  fetchProductSalesOrderDropdownForQuotationNumber,
  fetchProductQuotationById
} from "slices/sales_order/thunk";
import {
  fetchSubcategories,
  fetchMakes,
  fetchModels,
  fetchCategories,
} from "slices/enquiry/thunk";
// import {  fetchProductQuotationById } from "slices/sales_order/thunk";
// 4. Common Dropdown Thunks
import { fetchCompanyByDropDown } from "slices/Masters/company/thunk";
import { fetchAllUsersDropdown } from "slices/UserManagement/user/thunk";
import AddProductAndServicesSalesOrderForm from "./MasterForm";
import type { AddProductAndServicesSalesOrderFormProps } from "./MasterForm";

import { fetchProductsAndServicesByDropDown } from "slices/Masters/productsandservices/thunk";
import { fetchProductEnquiryDropdown } from "slices/enquiry/thunk";
import {
  SalesOrderFormData,
  salesOrderFormData,
} from "Common/CommonEnquiryData/SalesOrderForm";
import {
  // fetchAllDropdowns,
  fetchOrganisations,
} from "slices/UserManagement/organisation/thunk";
import { SortingState } from "@tanstack/react-table";
import { clearAllTableStates, clearTableState } from "utils/clearTableStates";

const ProductSalesOrderForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id, sales_order_type } = useParams<{
    id: string;
    sales_order_type: string;
  }>();
  const isEdit = Boolean(id);

  // --- State Management ---
  const [initialData, setInitialData] =
    useState<SalesOrderFormData>(salesOrderFormData);
  const [companies, setCompanies] = useState<any[]>([]);
  const [quotationReferences, setQuotationReferences] = useState<any[]>([]);
  const [organizationsState, setOrganizationsState] = useState<any[]>([]);
  // --- Redux Selectors ---
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  // 1. Selectors for Item Dropdown state (Categories, Makes, Models)
  const selectItemDropdowns = createSelector(
    (state: any) => state.Enquiry,
    (enquiry) => ({
      categories: enquiry.categories,
      sub_categories: enquiry.subCategories,
      makes: enquiry.makes,
      models: enquiry.models,
      products: enquiry.products,
    })
  );

  const { categories, sub_categories, makes, models, products } =
    useSelector(selectItemDropdowns);

  // 2. Company Dropdown Data
  const selectCompanyData = createSelector(
    (state: any) => state.Company,
    (company) => ({ companiesData: company.companyList || [] })
  );
  const { companiesData } = useSelector(selectCompanyData);

  // 3. Users Dropdown Data (Assigned Members)
  const selecetedUsers = createSelector(
    (state: any) => state.users,
    (user) => ({
      usersFormDropDowns: user.userFormDropDowns || [],
    })
  );
  const { usersFormDropDowns } = useSelector(selecetedUsers);

  // --- Side Effects (Data Fetching) ---

  // Update local state when Redux data is ready
  useEffect(() => {
    setCompanies(companiesData);
    handleFetchOrganization();
  }, [companiesData]);

  // Fetch initial data (Dropdowns and Quotation Detail/Enquiry Detail)
  useEffect(() => {
    const init = async () => {
      // Fetch global dropdown data
      dispatch(fetchCompanyByDropDown());
      dispatch(fetchCategories("products"));
      dispatch(fetchAllUsersDropdown());

      try {
        const qrefs = await dispatch(
          fetchProductSalesOrderDropdownForQuotationNumber()
        ).unwrap();
        // Assuming the API returns a list of objects with { id: string, quotation_number: string }
        setQuotationReferences(qrefs?.data || qrefs);
      } catch (err) {
        console.error("Failed to load quotation references:", err);
      }


    };
    init();
  }, [dispatch]);

  // --- Dynamic Dropdown Fetchers (Passed as Prop) ---

  // Fetch subcategories when a category is selected
  // const fetchServiceSubcategories = (categoryId: string) =>
  //   dispatch(
  //     fetchSubcategories({ item_type: "product", categoryId: categoryId })
  //   );

  // // Fetch makes when subcategory is selected (for products)
  // const fetchServiceMakes = (params: {
  //   categoryId: string;
  //   subcategoryId: string;
  // }) => {
  //   dispatch(fetchMakes(params));
  // };

  // // Fetch models when make is selected (for products)
  // const fetchServiceModels = (params: {
  //   categoryId: string;
  //   subcategoryId: string;
  //   makeId: string;
  // }) => {
  //   dispatch(fetchModels(params));
  // };

  // --- API Handler (Passed as Prop) ---
  const handleSaveProductSalesOrder = async (
    payload: any,
    action: "draft" | "save"
  ) => {
    try {

      await dispatch(
        createProductSalesOrder({ ...payload, action })

      ).unwrap();
      clearAllTableStates()
      toast.success("Sales Order created successfully");

      navigate("/sales_order/product/sales_order_log");
    } catch (err: any) {
      // Extract error message from various possible error structures
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data ||
        err?.message ||
        (typeof err === 'string' ? err : "Product Sales Order operation failed");

      // Throw the error for the MasterForm to handle loading state
      throw new Error(errorMessage);
    }
  };

  const handleFetchOrganization = async () => {
    try {
      const resData = await dispatch(
        fetchOrganisations({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      ).unwrap();
      setOrganizationsState(resData?.data || []);
    } catch (err) {
      console.error("Failed to load enquiry references:", err);
    }
  };

  const handleQuotationSelect = async (quotationId: string) => {
    try {
      const resData = await dispatch(
        fetchProductQuotationById(quotationId)
      ).unwrap();
      // console.log("resData on quotation select", resData);
      setInitialData(resData?.data?.data || resData?.data || resData); // Set the form data
    } catch (err: any) {
      // console.error("Failed to fetch enquiry data by reference:", err);
      // toast.error(err?.message || "Failed to load enquiry data.");
      setInitialData(salesOrderFormData); // Reset form on failure
    }
  };

  // --- Loading State Check (Crucial for Edit Mode) ---
  if (isEdit && !initialData) {
    // Check a nested detail to confirm data structure loaded
    return (
      <div className="d-flex justify-content-center mt-5">
        <p>Loading Sales Order...</p>
      </div>
    );
  }

  // Use a defined set of sources if not dynamically fetched
  const commonSources = [
    "Website",
    "Email",
    "Phone Call",
    "Referral",
    "Internal Reference",
  ];


  return (
    <div className="mt-10">
      <AddProductAndServicesSalesOrderForm
        initialData={initialData}
        companies={companies}
 
        onSave={handleSaveProductSalesOrder} // Service-specific handler
        quotationRefenrence={quotationReferences}
        onQuotationSelect={handleQuotationSelect}
  
        membersOptions={usersFormDropDowns} // Assigned member options
        enquiryReferences={""}
        organizationsState={organizationsState}
      />
    </div>
  );
};

export default ProductSalesOrderForm;
